const makeProduct = ({
  id,
  name,
  category,
  subcategory,
  price,
  compareAtPrice,
  unit,
  milk = "—",
  texture,
  flavour,
  description,
  tags = [],
  bestseller = false,
  newArrival = false,
  stock = 18,
  bestBefore = "28 September 2027",
}) => ({
  id,
  slug: name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, ""),
  name,
  category,
  subcategory,
  price,
  compareAtPrice,
  unit,
  image: `/img/product-${String(id).padStart(2, "0")}.png`,
  images: [`/img/product-${String(id).padStart(2, "0")}.png`],
  description,
  attributes: {
    origin: "Hong Kong selection",
    milk,
    texture,
    flavour,
    storage: category === "Drinks" ? "Store cool and dry" : "Keep chilled at 2–5°C",
  },
  tags,
  bestseller,
  newArrival,
  stock,
  bestBefore,
  rating: Number((4.4 + ((id * 7) % 6) / 10).toFixed(1)),
  reviews: 12 + ((id * 17) % 87),
  packSizes: [unit],
});

const productData = [
  makeProduct({
    id: 1,
    name: "Mont d'Ardoise Alpine Reserve",
    category: "Cheese",
    subcategory: "Hard",
    price: 128,
    unit: "200g",
    origin: "Hong Kong selection",
    milk: "Raw cow's milk",
    texture: "Firm, crystalline",
    flavour: "Toasted hazelnut, meadow herbs",
    description:
      "A long-aged mountain wheel with a buttery centre, delicate crystals and a savoury finish made for generous sharing boards.",
    tags: ["hard", "cow", "aged", "wine-pairing"],
    bestseller: true,
  }),
  makeProduct({
    id: 2,
    name: "Velours Bloom-Rind Wheel",
    category: "Cheese",
    subcategory: "Soft",
    price: 72,
    unit: "180g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Bloomy, supple",
    flavour: "Cream, mushroom, fresh hay",
    description:
      "A small-format bloomy rind cheese that ripens from a clean lactic centre into an irresistibly flowing edge.",
    tags: ["soft", "cow", "creamy", "bestseller"],
    bestseller: true,
    bestBefore: "22 August 2027",
  }),
  makeProduct({
    id: 3,
    name: "Azure Cave Blue",
    category: "Cheese",
    subcategory: "Blue",
    price: 96,
    unit: "200g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Fudgy, blue-veined",
    flavour: "Pepper, cultured cream, walnut",
    description:
      "Cave-matured blue with balanced salinity and a mellow creamy body—bold enough for a cheeseboard, gentle enough for salads.",
    tags: ["blue", "cow", "cave-aged"],
    newArrival: true,
  }),
  makeProduct({
    id: 4,
    name: "Meadow Goat Cheese Log",
    category: "Cheese",
    subcategory: "Fresh",
    price: 58,
    unit: "150g",
    origin: "Hong Kong selection",
    milk: "Goat's milk",
    texture: "Silky, lightly chalky",
    flavour: "Citrus, grass, cultured cream",
    description:
      "A bright young goat cheese rolled by hand, with a snowy rind and lemony centre that lifts roasted vegetables and tartines.",
    tags: ["fresh", "goat", "vegetarian"],
    bestseller: true,
    bestBefore: "19 August 2027",
  }),
  makeProduct({
    id: 5,
    name: "Ember Smoked Melting Cheese",
    category: "Cheese",
    subcategory: "Melting",
    price: 88,
    compareAtPrice: 98,
    unit: "250g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Supple, superbly melting",
    flavour: "Beech smoke, browned butter",
    description:
      "Naturally smoked slices that melt into a glossy blanket over potatoes, sourdough or anything headed for the grill.",
    tags: ["melting", "smoked", "cow", "hot-deal"],
  }),
  makeProduct({
    id: 6,
    name: "Golden Pasture Reserve 14 Months",
    category: "Cheese",
    subcategory: "Hard",
    price: 148,
    unit: "300g",
    origin: "Hong Kong selection",
    milk: "Raw cow's milk",
    texture: "Dense, satin-smooth",
    flavour: "Brown butter, apricot, roasted nuts",
    description:
      "A balanced mid-aged mountain cheese selected for its long, rounded finish and beautiful melting character.",
    tags: ["hard", "cow", "aged", "bestseller"],
    bestseller: true,
  }),
  makeProduct({
    id: 7,
    name: "Petit Cendre Ash-Ripened Goat",
    category: "Cheese",
    subcategory: "Soft",
    price: 69,
    unit: "120g",
    origin: "Hong Kong selection",
    milk: "Goat's milk",
    texture: "Creamline with a chalky heart",
    flavour: "Mineral, lemon, toasted sesame",
    description:
      "A petite ash-coated round with a clean mineral rind and a graceful progression from tangy centre to silky creamline.",
    tags: ["soft", "goat", "ash-ripened"],
    newArrival: true,
  }),
  makeProduct({
    id: 8,
    name: "Valley Bloom Triple Cream",
    category: "Cheese",
    subcategory: "Soft",
    price: 82,
    unit: "200g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Plush, spoonable",
    flavour: "Sweet cream, mushroom, brioche",
    description:
      "An indulgent triple-cream wheel with a fine white rind and mousse-like centre, best served simply with warm bread.",
    tags: ["soft", "cow", "triple-cream"],
  }),
  makeProduct({
    id: 9,
    name: "Old Orchard Rustic Wheel",
    category: "Cheese",
    subcategory: "Hard",
    price: 108,
    unit: "250g",
    origin: "Hong Kong selection",
    milk: "Sheep's milk",
    texture: "Semi-firm, rustic",
    flavour: "Wild herbs, almond, cellar earth",
    description:
      "A farmhouse-style sheep's milk tomme with a natural rind, sweet nuttiness and a pleasantly rustic finish.",
    tags: ["hard", "sheep", "farmhouse"],
  }),
  makeProduct({
    id: 10,
    name: "Rosemary Aged Sheep Cheese",
    category: "Cheese",
    subcategory: "Hard",
    price: 118,
    unit: "220g",
    origin: "Hong Kong selection",
    milk: "Sheep's milk",
    texture: "Firm, fine-grained",
    flavour: "Rosemary, caramel, sea salt",
    description:
      "A savoury aged sheep's cheese with a rosemary-brushed rind and lingering caramel depth for antipasti or grating.",
    tags: ["hard", "sheep", "herb-rind"],
  }),
  makeProduct({
    id: 11,
    name: "Cloud Cream-Filled Cheese",
    category: "Cheese",
    subcategory: "Fresh",
    price: 68,
    unit: "125g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Delicate shell, creamy centre",
    flavour: "Fresh milk, sweet cream",
    description:
      "Hand-knotted fresh cheese filled with soft curds and cream; tear over tomatoes, peaches or a drizzle of grassy olive oil.",
    tags: ["fresh", "cow", "vegetarian"],
    bestseller: true,
    stock: 9,
    bestBefore: "17 August 2027",
  }),
  makeProduct({
    id: 12,
    name: "Copper Rind Washed Wheel",
    category: "Cheese",
    subcategory: "Soft",
    price: 92,
    unit: "180g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Supple, yielding",
    flavour: "Cultured butter, yeast, savoury broth",
    description:
      "A characterful washed-rind wheel with a copper glow, rounded aroma and deeply savoury yet balanced paste.",
    tags: ["soft", "cow", "washed-rind"],
  }),
  makeProduct({
    id: 13,
    name: "Black Truffle Alpine",
    category: "Cheese",
    subcategory: "Hard",
    price: 168,
    unit: "200g",
    origin: "Hong Kong selection",
    milk: "Cow's milk",
    texture: "Firm, silky",
    flavour: "Black truffle, hazelnut, sweet cream",
    description:
      "A refined alpine cheese layered with aromatic black truffle for an effortlessly celebratory cheeseboard centrepiece.",
    tags: ["hard", "cow", "truffle", "gifting"],
    newArrival: true,
  }),
  makeProduct({
    id: 14,
    name: "Sea Salt Grilling Cheese",
    category: "Cheese",
    subcategory: "Melting",
    price: 62,
    unit: "225g",
    origin: "Hong Kong selection",
    milk: "Goat and sheep's milk",
    texture: "Springy, grillable",
    flavour: "Clean milk, mint, sea salt",
    description:
      "A firm brined cheese made for the grill, with a golden crust, satisfying bite and a clean minty lift.",
    tags: ["melting", "goat", "sheep", "grilling"],
  }),
  makeProduct({
    id: 15,
    name: "Farmhouse Cultured Butter",
    category: "Grocery",
    subcategory: "Dairy",
    price: 48,
    unit: "250g",
    origin: "Hong Kong selection",
    milk: "Cow's cream",
    texture: "Silky, pliable",
    flavour: "Cultured cream, sea salt",
    description:
      "Slow-cultured butter churned in small batches for a gentle tang and deep creaminess on bread or in pastry.",
    tags: ["dairy", "butter", "baking"],
    bestseller: true,
    bestBefore: "8 October 2027",
  }),
  makeProduct({
    id: 16,
    name: "Heritage Coppa",
    category: "Meat & Seafood",
    subcategory: "Cold Cut",
    price: 86,
    unit: "100g",
    origin: "Hong Kong selection",
    texture: "Tender, finely marbled",
    flavour: "Juniper, pepper, sweet pork",
    description:
      "Air-cured pork collar sliced paper-thin, with gentle spice and beautiful marbling for antipasti and sandwiches.",
    tags: ["cold-cut", "pork", "charcuterie"],
  }),
  makeProduct({
    id: 17,
    name: "Forest Pepper Saucisson",
    category: "Meat & Seafood",
    subcategory: "Cold Cut",
    price: 78,
    unit: "180g",
    origin: "Hong Kong selection",
    texture: "Firm, rustic",
    flavour: "Black pepper, garlic, cured pork",
    description:
      "A traditional dry sausage rolled in cracked forest pepper, equally at home in a picnic basket or aperitif spread.",
    tags: ["cold-cut", "pork", "pepper"],
    bestseller: true,
  }),
  makeProduct({
    id: 18,
    name: "Beech-Smoked Country Ham",
    category: "Meat & Seafood",
    subcategory: "Cold Cut",
    price: 92,
    unit: "120g",
    origin: "Hong Kong selection",
    texture: "Lean, supple",
    flavour: "Beech smoke, juniper, sweet cure",
    description:
      "Thinly sliced country ham gently smoked over beechwood for an elegant, savoury counterpoint to ripe cheese.",
    tags: ["cold-cut", "smoked", "pork"],
  }),
  makeProduct({
    id: 19,
    name: "Duck Rillettes with Thyme",
    category: "Meat & Seafood",
    subcategory: "Terrines",
    price: 74,
    unit: "180g",
    origin: "Hong Kong selection",
    texture: "Rich, spreadable",
    flavour: "Duck confit, thyme, white pepper",
    description:
      "Slow-cooked duck folded with its own juices and garden thyme, ready to spread thickly on toasted sourdough.",
    tags: ["terrine", "duck", "aperitif"],
    newArrival: true,
  }),
  makeProduct({
    id: 20,
    name: "Fig & Walnut Preserve",
    category: "Grocery",
    subcategory: "Condiments",
    price: 52,
    unit: "210g",
    origin: "Hong Kong selection",
    texture: "Jammy with walnut pieces",
    flavour: "Ripe fig, citrus peel, walnut",
    description:
      "A lower-sugar preserve simmered with toasted walnuts, designed to sit alongside blue and aged cheeses.",
    tags: ["condiment", "fig", "cheese-pairing"],
  }),
  makeProduct({
    id: 21,
    name: "Apricot Lavender Confit",
    category: "Grocery",
    subcategory: "Sweets",
    price: 49,
    unit: "210g",
    origin: "Hong Kong selection",
    texture: "Soft-set preserve",
    flavour: "Apricot, lavender, lemon",
    description:
      "Sun-ripe apricots lightly perfumed with lavender; bright on breakfast toast and especially lovely with goat cheese.",
    tags: ["sweet", "apricot", "cheese-pairing"],
  }),
  makeProduct({
    id: 22,
    name: "Rosemary Sea-Salt Crackers",
    category: "Grocery",
    subcategory: "Bakery",
    price: 45,
    unit: "160g",
    origin: "Hong Kong selection",
    texture: "Thin, crisp",
    flavour: "Olive oil, rosemary, sea salt",
    description:
      "Delicate olive-oil crackers baked until blistered and crisp, sturdy enough for soft cheese but never overpowering it.",
    tags: ["bakery", "crackers", "vegetarian"],
  }),
  makeProduct({
    id: 23,
    name: "Stone-Baked Walnut Loaf",
    category: "Grocery",
    subcategory: "Bakery",
    price: 46,
    unit: "420g",
    origin: "Hong Kong selection",
    texture: "Crackled crust, open crumb",
    flavour: "Sourdough, toasted walnut",
    description:
      "A naturally leavened local loaf with a dark crust and generous walnuts, baked fresh for cheese-board delivery days.",
    tags: ["bakery", "bread", "local"],
    stock: 7,
    bestBefore: "15 August 2027",
  }),
  makeProduct({
    id: 24,
    name: "Citrus-Marinated Olives",
    category: "Grocery",
    subcategory: "Appetizer",
    price: 45,
    unit: "250g",
    origin: "Hong Kong selection",
    texture: "Plump, firm",
    flavour: "Orange peel, coriander, brine",
    description:
      "Green and purple olives tumbled with citrus peel and coriander seed for a bright, ready-to-serve aperitif bowl.",
    tags: ["appetizer", "olives", "vegan"],
  }),
  makeProduct({
    id: 25,
    name: "Market Pear & Muscat Box",
    category: "Fruit & Veg",
    subcategory: "Fruits",
    price: 88,
    unit: "1.2kg box",
    origin: "Hong Kong selection",
    texture: "Juicy, crisp",
    flavour: "Floral pear, honeyed grape",
    description:
      "A seasonal pairing box of fragrant pears and Muscat grapes selected to serve beside a mixed cheese plate.",
    tags: ["fruit", "seasonal", "cheese-pairing"],
    newArrival: true,
  }),
  makeProduct({
    id: 26,
    name: "Woodland Mushroom Selection",
    category: "Fruit & Veg",
    subcategory: "Mushrooms",
    price: 76,
    unit: "300g box",
    origin: "Hong Kong selection",
    texture: "Meaty, tender",
    flavour: "Woodland, nutty, savoury",
    description:
      "A cook's selection of oyster, chestnut and shiitake mushrooms for gratins, omelettes and alpine cheese toasties.",
    tags: ["vegetables", "mushrooms", "seasonal"],
  }),
  makeProduct({
    id: 27,
    name: "Orchard Sparkling Apple",
    category: "Drinks",
    subcategory: "Juice",
    price: 56,
    unit: "750ml",
    origin: "Hong Kong selection",
    texture: "Fine bubbles",
    flavour: "Tart apple, blossom, dry finish",
    description:
      "A celebratory non-alcoholic sparkling apple drink pressed from bittersweet orchard fruit with no added sugar.",
    tags: ["drink", "non-alcoholic", "apple"],
    bestseller: true,
    bestBefore: "12 February 2027",
  }),
  makeProduct({
    id: 28,
    name: "Alpine Botanical Tonic",
    category: "Drinks",
    subcategory: "Soft Drinks",
    price: 45,
    unit: "4 × 200ml",
    origin: "Hong Kong selection",
    texture: "Bright carbonation",
    flavour: "Gentian, lemon peel, mountain herbs",
    description:
      "A crisp botanical tonic with gentle bitterness and fresh citrus, made for aperitif hour or drinking over ice.",
    tags: ["drink", "non-alcoholic", "botanical"],
    newArrival: true,
    bestBefore: "8 March 2027",
  }),
];

export const productCategories = [
  "All",
  "Cheese",
  "Meat & Seafood",
  "Fruit & Veg",
  "Grocery",
  "Drinks",
];

export const productSubcategories = [
  "Blue",
  "Fresh",
  "Hard",
  "Melting",
  "Soft",
  "Cold Cut",
  "Terrines",
  "Dairy",
  "Bakery",
  "Condiments",
  "Fruits",
  "Mushrooms",
  "Juice",
];

export default productData;
